import type { Company, CompanyAttendanceMode, CompanyOperationMode } from '@/types';

type CompanyModeSource = {
  operationMode?: CompanyOperationMode | null;
  attendanceMode?: CompanyAttendanceMode | null;
};

export const COMPANY_OPERATION_MODE_LABELS: Record<CompanyOperationMode, string> = {
  SCHEDULE_ONLY: 'Somente agenda',
  QUEUE_ONLY: 'Somente fila',
  HYBRID: 'Agenda + fila',
};

const DEFAULT_COMPANY_OPERATION_MODE: CompanyOperationMode = 'HYBRID';

function normalizeCompanyOperationMode(
  mode?: CompanyOperationMode | CompanyAttendanceMode | null,
): CompanyOperationMode {
  if (mode === 'WAITLIST_ONLY') {
    return 'QUEUE_ONLY';
  }

  return mode ?? DEFAULT_COMPANY_OPERATION_MODE;
}

export function resolveCompanyOperationMode(
  companyOrMode?: CompanyModeSource | CompanyOperationMode | CompanyAttendanceMode | null,
): CompanyOperationMode {
  if (typeof companyOrMode === 'string' || companyOrMode == null) {
    return normalizeCompanyOperationMode(companyOrMode);
  }

  return normalizeCompanyOperationMode(companyOrMode.operationMode ?? companyOrMode.attendanceMode);
}

export function resolveCompanyAttendanceMode(mode?: CompanyAttendanceMode | null): CompanyOperationMode {
  return normalizeCompanyOperationMode(mode);
}

export function companySupportsSchedule(
  companyOrMode?: CompanyModeSource | CompanyOperationMode | CompanyAttendanceMode | null,
) {
  return resolveCompanyOperationMode(companyOrMode) !== 'QUEUE_ONLY';
}

export function companySupportsWaitingList(
  companyOrMode?: CompanyModeSource | CompanyOperationMode | CompanyAttendanceMode | null,
) {
  return resolveCompanyOperationMode(companyOrMode) !== 'SCHEDULE_ONLY';
}

function getCompanyOperationModeLabel(
  companyOrMode?: CompanyModeSource | CompanyOperationMode | CompanyAttendanceMode | null,
) {
  return COMPANY_OPERATION_MODE_LABELS[resolveCompanyOperationMode(companyOrMode)];
}

export function getCompanyAttendanceModeLabel(mode?: CompanyAttendanceMode | null) {
  return getCompanyOperationModeLabel(mode);
}

export function buildCompanyDisplayAddress(
  company: Pick<Company, 'street' | 'number' | 'complement' | 'neighborhood' | 'postalCode'>,
): string | null {
  const parts = [company.street, company.number, company.complement, company.neighborhood, company.postalCode]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean);

  if (parts.length === 0) return null;

  return parts.join(', ');
}
